import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Loader2, Send, ArrowRight, Undo2, Download } from "lucide-react";

const yesterday = () => new Date(Date.now() + 330 * 60000 - 86400000).toISOString().split("T")[0];
const TONE = { invoice_no: "bg-blue-100 text-blue-800", tracking: "bg-violet-100 text-violet-800", mode: "bg-amber-100 text-amber-800" };

const UndoCell = ({ c, onUndone }) => {
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState("");
  if (c.undone_by) return <span className="text-[11px] font-semibold text-emerald-700" data-testid={`changes-undone-${c.audit_id}-${c.field}`}>Undone by {c.undone_by}</span>;
  if (blocked) return <span className="text-[11px] font-semibold text-rose-600" data-testid={`changes-blocked-${c.audit_id}-${c.field}`}>{blocked}</span>;
  const undo = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/reports/changes-review/undo`, { audit_id: c.audit_id, field: c.field }, { headers: getAuthHeader() });
      toast.success(`${c.label} put back to ${c.before} (${r.data.rows} row${r.data.rows === 1 ? "" : "s"})`);
      onUndone(c, r.data.undone_by);
    } catch (e) {
      const msg = e.response?.data?.detail || "Undo failed";
      if (e.response?.status === 409) setBlocked(msg); else toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={undo} disabled={busy} data-testid={`changes-undo-${c.audit_id}-${c.field}`}
      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700"
      title={`Put back ${c.before}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Undo
    </button>
  );
};

const PersonCard = ({ p, onUndone }) => (
  <div className="rounded-xl border border-slate-200 bg-white" data-testid={`changes-card-${p.email}`}>
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-xl">
      <span className="font-semibold text-slate-800">{p.name}</span>
      <span className="text-[11px] text-slate-500">{p.email}</span>
      <span className="ml-auto flex flex-wrap gap-1.5 text-[11px]">
        {Object.entries(p.counts).map(([f, n]) => n > 0 && (
          <span key={f} className={`rounded-full px-2 py-0.5 font-semibold ${TONE[f]}`}>{n} {f === "invoice_no" ? "invoice" : f === "mode" ? "service" : "tracking"}</span>
        ))}
        {p.total_fills > 0 && (
          <span className="rounded-full px-2 py-0.5 bg-slate-100 text-slate-600" data-testid={`changes-fills-${p.email}`}
            title={`First-time fills — invoice ${p.fills.invoice_no}, tracking ${p.fills.tracking}, service ${p.fills.mode}`}>
            {p.total_fills} first-time fills
          </span>
        )}
      </span>
    </div>
    {p.changes.length === 0 ? <p className="px-3 py-2 text-xs text-slate-500">Only first-time fills — no existing values changed.</p> : (
      <div className="overflow-x-auto"><table className="w-full text-xs min-w-[640px]">
        <tbody>
          {p.changes.map((c, i) => (
            <tr key={i} className="border-t border-slate-100" data-testid={`changes-row-${p.email}-${i}`}>
              <td className="px-3 py-1.5 text-slate-500 w-14">{c.time}</td>
              <td className="px-2 py-1.5 w-20"><span className={`rounded px-1.5 py-0.5 font-semibold ${TONE[c.field]}`}>{c.label}</span></td>
              <td className="px-2 py-1.5 font-mono">
                <span className="line-through text-rose-600">{c.before}</span>
                <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />
                {c.kind === "removed" ? <span className="font-sans font-semibold text-rose-700">removed</span> : <span className="text-emerald-700 font-semibold">{c.after}</span>}
              </td>
              <td className="px-2 py-1.5 text-slate-700">{c.customer}{c.code && <span className="text-slate-400"> · {c.code}</span>}</td>
              <td className="px-2 py-1.5 text-slate-500">{c.invoice_no && `Inv ${c.invoice_no}`}</td>
              <td className="px-3 py-1.5 text-right whitespace-nowrap"><UndoCell c={c} onUndone={onUndone} /></td>
            </tr>
          ))}
        </tbody>
      </table></div>
    )}
  </div>
);

const ExcelRange = () => {
  const y = yesterday();
  const [from, setFrom] = useState(new Date(new Date(y).getTime() - 6 * 86400000).toISOString().split("T")[0]);
  const [to, setTo] = useState(y);
  const dl = async () => {
    try {
      const r = await axios.get(`${API}/reports/changes-review/excel`, { headers: getAuthHeader(), params: { date_from: from, date_to: to }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `Changes_Review_${from}_${to}.xlsx`;
      a.click();
    } catch { toast.error("Excel download failed — pick up to 93 days"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="changes-excel-range">
      <span className="text-slate-500">Excel</span>
      <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-lg" data-testid="changes-excel-from" />
      <span className="text-slate-400">→</span>
      <input type="date" value={to} onChange={e => setTo(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-lg" data-testid="changes-excel-to" />
      <button onClick={dl} data-testid="changes-excel-btn" className="inline-flex items-center gap-1 px-3 py-1.5 font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
        <Download className="w-3.5 h-3.5" /> Download
      </button>
    </div>
  );
};

// Admin: who changed existing invoice / tracking / service values on a day.
export const ChangesReview = () => {
  const [date, setDate] = useState(yesterday());
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    axios.get(`${API}/reports/changes-review`, { headers: getAuthHeader(), params: { date } })
      .then(r => setD(r.data)).catch(e => { toast.error(e.response?.data?.detail || "Could not load changes"); setD({ people: [] }); });
  }, [date]);
  const markUndone = (c, by) => setD(prev => ({
    ...prev, people: prev.people.map(p => ({ ...p, changes: p.changes.map(x =>
      x.audit_id === c.audit_id && x.field === c.field ? { ...x, undone_by: by } : x) })) }));
  const sendNow = async () => {
    try {
      const r = await axios.post(`${API}/reports/changes-review/send-now`, {}, { headers: getAuthHeader() });
      toast.success(`Yesterday's changes summary sent to ${r.data.sent_to} admin(s)`);
    } catch { toast.error("Could not send"); }
  };
  return (
    <div className="p-4 pt-2 space-y-3" data-testid="changes-review">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="changes-date" className="px-2 py-1 border border-slate-300 rounded-lg" />
        {d?.date && <span className="text-slate-600" data-testid="changes-total"><b className={d.total_changes ? "text-rose-600" : "text-emerald-600"}>{d.total_changes}</b> change(s) · {d.total_fills} first-time fill(s)</span>}
        <ExcelRange />
        <button onClick={sendNow} data-testid="changes-send-now" className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white">
          <Send className="w-3.5 h-3.5" /> Send yesterday's summary now
        </button>
      </div>
      {!d ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        : d.people.length === 0 ? <p className="p-6 text-center text-sm text-emerald-700" data-testid="changes-empty">No invoice, tracking or service changes on this day.</p>
        : d.people.map(p => <PersonCard key={p.email} p={p} onUndone={markUndone} />)}
      <p className="text-[11px] text-slate-500">Lists edits to existing values (e.g. invoice A001 → A002, tracking edited/removed, service EMS → ITPS). First-time fills are counted only. A summary is sent to every admin's bell at 11:55 AM IST for the previous day.</p>
    </div>
  );
};
